<script>
import Address from "@/components/_Shared/Addresses/Index";
import InputGroup from "@/components/_Shared/InputGroup";
import phoneFormatter from "@/utils/formatters/phones";
import stringHelper from "@/utils/helpers/string";
import { mapState, mapActions } from "vuex";
import dateMixin from "@/mixins/dateMixin";

export default {
  mixins: [dateMixin],
  data() {
    return {
      loader: false,
      preview_image: "",
      file: null,
      first_name: "",
      last_name: "",
      city_name: "",
      state_name: "",
      phone: ""
    };
  },
  components: {
    Address,
    InputGroup
  },
  computed: {
    ...mapState("user", ["user"])
  },
  methods: {
    ...mapActions("user", ["uploadProfilePicture", "updatePersonalData"]),

    getPersonalData() {
      this.preview_image = this.user.profile.profile_picture;
      this.first_name = this.user.first_name;
      this.last_name = this.user.last_name;
      this.city_name = this.user.profile.city_name;
      this.state_name = this.user.profile.state_name;
      this.phone = phoneFormatter.getPhone(this.user.phones[0]);
    },

    changeFile(e) {
      try {
        const fileTypeIsAccepted = type => {
          const acceptedTypes = ["image/png", "image/jpeg"];
          return acceptedTypes.includes(type);
        };

        const file = e.target.files[0];

        if (!fileTypeIsAccepted(file.type))
          throw "Ops! O tipo de arquivo inserido não é aceito.";

        this.preview_image = URL.createObjectURL(file);
        this.file = file;
        this.submitFile();
      } catch (e) {
        this.$toasted.error(e);
      }
    },

    async submitFile() {
      this.loader = true;

      try {
        await this.uploadProfilePicture(this.file);
        this.$toasted.success("Imagem alterada com sucesso!");
      } catch (e) {
        this.$toasted.error(e);
      }

      this.loader = false;
    },

    async submit() {
      this.loader = true;

      try {
        let data = {
          first_name: stringHelper.capitalize(this.first_name),
          last_name: stringHelper.capitalize(this.last_name),
          city_name: stringHelper.capitalize(this.city_name),
          state_name: this.state_name.toUpperCase(),
          phone: phoneFormatter.createPhoneObject(this.phone)
        };

        await this.updatePersonalData(data);
        this.$toasted.success("Dados alterados com sucesso!");
      } catch (e) {
        this.$toasted.error(e);
      }

      this.loader = false;
    }
  },
  watch: {
    user(user) {
      this.getPersonalData();
    }
  },
  mounted() {
    this.getPersonalData();
  }
};
</script>

<template>
  <div class="container">
    <div class="card">
      <form @submit.prevent="submit">
        <h1 class="title">Dados pessoais</h1>
        <div class="row">
          <div class="left">
            <input type="file" class="hidden" id="photo" @change="changeFile" accept="image/*" />

            <label class="photo" for="photo">
              <img :src="preview_image" width="100%" />
              <div class="overlay"></div>
            </label>
          </div>

          <div class="right">
            <div class="form-group">
              <InputGroup
                :error="errors.first('nome')"
                v-validate="'required'"
                v-model="first_name"
                label="nome"
                custom-classes="capitalize"
              />

              <InputGroup
                :error="errors.first('sobrenome')"
                v-validate="'required'"
                v-model="last_name"
                label="sobrenome"
                custom-classes="capitalize"
              />
            </div>

            <div class="form-group">
              <InputGroup :disabled="true" :value="user.email" label="e-mail" />

              <InputGroup
                :disabled="true"
                :value="user.profile.birthdate | formatDate('DD/MM/Y')"
                label="data de nascimento"
                mask="##/##/####"
              />

              <InputGroup
                v-if="user.accountable.cpf"
                :disabled="true"
                :value="user.accountable.cpf"
                label="cpf"
                mask="###.###.###-##"
              />

              <InputGroup
                v-else-if="user.accountable.cnpj"
                :disabled="true"
                :value="user.accountable.cnpj"
                label="cnpj"
                mask="##.###.###/####-##"
              />
            </div>

            <div class="form-group">
              <InputGroup
                :error="errors.first('city_name')"
                v-validate="'required'"
                v-model="city_name"
                label="cidade"
              />

              <InputGroup
                :error="errors.first('state_name')"
                v-validate="'required|length:2'"
                v-model="state_name"
                label="UF"
              />

              <InputGroup
                :error="errors.first('phone')"
                v-validate="'required'"
                v-model="phone"
                label="celular"
                :mask="['(##) ####-####', '(##) #####-####']"
              />
            </div>
          </div>
        </div>

        <div class="btn-group">
          <button
            type="submit"
            class="btn bg-purple"
            :disabled="loader"
            :class="{'btn--loading': loader}"
          >
            <span>enviar</span>
          </button>
        </div>
      </form>
    </div>
    <div class="card">
      <h1 class="title">Endereços</h1>
      <Address />
    </div>
  </div>
</template>

<style scoped>
.hidden {
  display: none;
}

.btn-group {
  justify-content: flex-end;
}

.card {
  margin: 10px 0;
}

.row {
  align-items: center;
}

.right {
  flex: 1;
  margin-left: 20px;
}

.photo {
  width: 150px;
  height: 150px;
  border-radius: 100%;
  background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.8) 0,
      rgba(255, 255, 255, 0.8) 100%
    ),
    url("../../static/user-solid.png") no-repeat center bottom #fff;
  background-size: 120px 120px;
  text-align: center;
  line-height: 200px;
  box-shadow: 0 0 0 6px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.photo img {
  width: 100%;
  height: 100%;
  border-radius: 100%;
  object-fit: cover;
  object-position: center;
  transition: all 0.25s ease;
}

.photo [src="#"] {
  opacity: 0;
}

.photo .overlay {
  background: url("../../static/edit-photo.svg") center no-repeat;
  background-color: rgba(255, 255, 255, 0.8);
  background-size: 70px;
  border-radius: 100%;
  width: 150px;
  height: 150px;
  margin-top: -150px;
  opacity: 0;
  cursor: pointer;
  transition: all 0.25s ease;
}

.photo:hover .overlay {
  opacity: 1;
}

@media (max-width: 480px) {
  .row {
    flex-direction: column !important;
  }
  .right {
    margin-left: 0;
    margin-bottom: 30px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PersonalData.vue